import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_API_URL;

interface Match {
    id: number;
    squadId: number;
    squadName: string;
    squadDate: string;
    team1Id: number;
    team2Id: number;
    team1Name: string;
    team2Name: string;
    scheduledTime: string;
    result?: string;
    team1Lane?: number;
    team2Lane?: number;
}

interface TeamMember {
    id: number;
    teamId: number;
    playerId: number;
    playerName: string;
    average: number;
    handicap: number;
    sortOrder: number;
}

interface MatchResults {
    team1SeriesTotal: number;
    team2SeriesTotal: number;
    team1SeriesHandicap: number;
    team2SeriesHandicap: number;
    team1Points: number;
    team2Points: number;
    gameWinners: number[];
    seriesWinner: number;
    seriesWinnerHandicap: number;
}

interface MatchScoringModalProps {
    matchId: number;
    leagueId: string;
    onClose: () => void;
}

const MatchScoringModal: React.FC<MatchScoringModalProps> = ({ matchId, leagueId, onClose }) => {
    const { t } = useTranslation();
    const [match, setMatch] = useState<Match | null>(null);
    const [team1Members, setTeam1Members] = useState<TeamMember[]>([]);
    const [team2Members, setTeam2Members] = useState<TeamMember[]>([]);
    const [bakerScores, setBakerScores] = useState<Record<string, number>>({});
    const [gamesCount, setGamesCount] = useState(3);
    const [scores, setScores] = useState<Record<string, number | undefined>>({});
    const [scoresHandicap, setScoresHandicap] = useState<Record<string, number | undefined>>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [matchResults, setMatchResults] = useState<MatchResults | null>(null);
    const [backupInfo, setBackupInfo] = useState<Record<string, any>>({});
    const [loadedTeamGameTotals, setLoadedTeamGameTotals] = useState<Record<string, { team1Total: number; team2Total: number; team1Scratch: number; team2Scratch: number }>>({});
    const [blindConfig, setBlindConfig] = useState<{ botBlindAverage: number | null } | null>(null);

    useEffect(() => {
        const fetchMatchData = async () => {
            setLoading(true);
            setError('');
            try {
                const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

                // Fetch match details
                const matchResponse = await fetch(`${apiUrl}/player/scoring/matches/${matchId}`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });
                if (!matchResponse.ok) throw new Error('Failed to fetch match data');
                const matchData = await matchResponse.json();
                setMatch(matchData);

                // Fetch games count for this squad
                const gamesResponse = await fetch(`${apiUrl}/player/schedule/${leagueId}/games-count/${matchData.squadId}`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });
                if (gamesResponse.ok) {
                    const { gamesCount: count } = await gamesResponse.json();
                    setGamesCount(count || 3);
                }

                // Fetch team members for both teams
                // Skip team2 if it's Bot Blind (team2Id === 0)
                const isBotBlindMatch = matchData.team2Id === 0;
                
                const team1Response = await fetch(`${apiUrl}/player/scoring/teams/${matchData.team1Id}/members`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });

                let team2Response = null;
                if (!isBotBlindMatch) {
                    team2Response = await fetch(`${apiUrl}/player/scoring/teams/${matchData.team2Id}/members`, {
                        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                    });
                }

                if (team1Response.ok && (isBotBlindMatch || (team2Response && team2Response.ok))) {
                    const team1Data = await team1Response.json();
                    const team2Data = isBotBlindMatch ? { members: [] } : await team2Response!.json();
                    setTeam1Members(team1Data.members || []);
                    setTeam2Members(team2Data.members || []);
                }

                // Fetch league handicap config for Bot Blind average
                const configResponse = await fetch(`${apiUrl}/leagues/${leagueId}/handicap-config`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });
                if (configResponse.ok) {
                    const configData = await configResponse.json();
                    setBlindConfig(configData.handicapConfig || null);
                }

                // Fetch scores and results
                const scoresResponse = await fetch(`${apiUrl}/player/scoring/match/${matchId}/scores`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });
                if (scoresResponse.ok) {
                    const scoresData = await scoresResponse.json();
                    setScores(scoresData.scores || {});
                    setScoresHandicap(scoresData.scoresHandicap || {});
                    setMatchResults(scoresData.matchResults || null);
                    setBakerScores(scoresData.bakerScores || {});
                    setBackupInfo(scoresData.backupInfo || {});
                    setLoadedTeamGameTotals(scoresData.teamGameTotals || {});
                }
            } catch (err: any) {
                setError(err.message || t('player.failedToFetchMatchData'));
            } finally {
                setLoading(false);
            }
        };
        fetchMatchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matchId, leagueId]);

    // Helpers
    const calculatePlayerSeries = (playerId: number, withHandicap = false) => {
        let total = 0;
        for (let game = 1; game <= gamesCount; game++) {
            const scoreKey = `${playerId}-${game}`;
            
            if (withHandicap && scoresHandicap[scoreKey] !== undefined) {
                // Use pre-calculated score with handicap from database (includes backup handicap)
                total += scoresHandicap[scoreKey]!;
            } else {
                const score = scores[scoreKey];
                if (score !== undefined && score !== null && score >= 0) {
                    total += score;
                    if (withHandicap) {
                        // Fallback: Calculate with current handicap (only if not using stored values)
                        const player = [...team1Members, ...team2Members].find(p => p.playerId === playerId);
                        if (player) total += player.handicap;
                    }
                }
            }
        }
        return total;
    };

    const calculateTeamGameTotal = (teamMembers: TeamMember[], gameNumber: number, withHandicap = false) => {
        // Try baker score first
        const teamId = teamMembers[0]?.teamId;
        const bakerKey = `team${teamId === match?.team1Id ? '1' : '2'}-${gameNumber}`;
        if (bakerScores[bakerKey] !== undefined) {
            return bakerScores[bakerKey];
        }
        
        // If we have loaded totals from database, use them (scratch or handicap)
        if (loadedTeamGameTotals[gameNumber]) {
            const isTeam1 = teamId === match?.team1Id;
            if (withHandicap) {
                // Use handicap total (pre-calculated with backup handicaps baked in)
                return isTeam1 ? loadedTeamGameTotals[gameNumber].team1Total : loadedTeamGameTotals[gameNumber].team2Total;
            } else {
                // Use scratch total from database
                return isTeam1 ? loadedTeamGameTotals[gameNumber].team1Scratch : loadedTeamGameTotals[gameNumber].team2Scratch;
            }
        }
        
        // Fallback: sum individual scores (only if no database totals available)
        let total = 0;
        teamMembers.forEach(member => {
            const score = scores[`${member.playerId}-${gameNumber}`];
            if (score !== undefined && score !== null && score >= 0) {
                total += score;
                if (withHandicap) total += member.handicap;
            }
        });
        return total;
    };

    const calculateTeamSeries = (teamMembers: TeamMember[], withHandicap = false) => {
        let total = 0;
        for (let game = 1; game <= gamesCount; game++) {
            total += calculateTeamGameTotal(teamMembers, game, withHandicap);
        }
        return total;
    };

    const getGameWinner = (gameNumber: number) => {
        if (!matchResults) return null;
        return matchResults.gameWinners[gameNumber - 1];
    };

    const getSeriesWinnerHandicap = () => {
        if (!matchResults) return null;
        return matchResults.seriesWinnerHandicap;
    };

    useEffect(() => {
        if (
            team1Members.length > 0 &&
            team2Members.length > 0 &&
            (Object.keys(scores).length > 0 || Object.keys(bakerScores).length > 0) &&
            gamesCount > 0
        ) {
            const gameWinners: number[] = [];
            let team1SeriesHandicap = 0;
            let team2SeriesHandicap = 0;

            for (let game = 1; game <= gamesCount; game++) {
                // Use baker scores if available
                const bakerKey1 = `team1-${game}`;
                const bakerKey2 = `team2-${game}`;
                let t1 = 0, t2 = 0;

                if (bakerScores[bakerKey1] !== undefined && bakerScores[bakerKey2] !== undefined) {
                    t1 = bakerScores[bakerKey1];
                    t2 = bakerScores[bakerKey2];
                } else if (loadedTeamGameTotals[game]) {
                    // Use pre-calculated handicap totals from database (includes backup handicaps)
                    t1 = loadedTeamGameTotals[game].team1Total;
                    t2 = loadedTeamGameTotals[game].team2Total;
                } else {
                    // Fallback: calculate with current handicaps (won't include backup handicaps correctly)
                    t1 = team1Members.reduce(
                        (sum, m) => sum + ((scores[`${m.playerId}-${game}`] ?? 0) + m.handicap),
                        0
                    );
                    t2 = team2Members.reduce(
                        (sum, m) => sum + ((scores[`${m.playerId}-${game}`] ?? 0) + m.handicap),
                        0
                    );
                }

                team1SeriesHandicap += t1;
                team2SeriesHandicap += t2;

                if (t1 > t2) gameWinners.push(1);
                else if (t2 > t1) gameWinners.push(2);
                else gameWinners.push(0);
            }

            let seriesWinnerHandicap = 0;
            if (team1SeriesHandicap > team2SeriesHandicap) seriesWinnerHandicap = 1;
            else if (team2SeriesHandicap > team1SeriesHandicap) seriesWinnerHandicap = 2;

            setMatchResults(prev => prev
                ? { ...prev, gameWinners, seriesWinnerHandicap }
                : {
                    team1SeriesTotal: 0, team2SeriesTotal: 0, team1SeriesHandicap, team2SeriesHandicap,
                    team1Points: 0, team2Points: 0, seriesWinner: 0, seriesWinnerHandicap, gameWinners
                }
            );
        }
    }, [team1Members, team2Members, scores, bakerScores, gamesCount, loadedTeamGameTotals]);

    // UI
    if (loading) {
        return (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl p-8 text-center text-blue-900">{t('player.loadingMatchData')}</div>
            </div>
        );
    }
    if (error || !match) {
        return (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl p-8 text-center text-red-600">{error || t('player.matchNotFound')}</div>
                <button onClick={onClose} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded">{t('common.close')}</button>
            </div>
        );
    }




    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
            <div
                className="bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800 rounded-xl shadow-2xl max-w-5xl w-full p-0 relative"
                style={{
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#6366f1 #18181b', // Tailwind indigo-500 thumb, zinc-900 track
                }}
            >
                {/* X Close Button */}
                <button
                    onClick={onClose}
                    className="sticky top-4 right-4 float-right text-gray-400 hover:text-blue-200 z-50"
                    title={t('common.close')}
                    style={{ position: 'sticky', float: 'right' }}
                >
                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
                {/* Header */}
                <div className="bg-white/10 backdrop-blur-lg border-b border-white/20 rounded-t-xl px-8 py-6">
                    <div className="flex flex-col md:flex-row md:justify-between md:items-center">
                        <div>
                            <h1 className="text-3xl font-bold text-white">{t('player.matchScoring')}</h1>
                            <p className="text-blue-200 mt-1">
                                {match.squadName} • {new Date(match.squadDate).toLocaleDateString()} • {match.scheduledTime}
                            </p>
                        </div>
                    </div>
                </div>
                {/* Match Details */}
                <div className="px-8 py-8">
                    <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 mb-6">
                        <div className="text-center">
                            <h2 className="text-2xl font-bold text-white mb-2">
                                {match.team1Name} {t('player.vs')} {match.team2Id === 0 ? (t('player.blind') || 'Blind') : match.team2Name}
                            </h2>
                            {match.team1Lane && match.team2Lane && match.team1Lane > 0 && match.team2Lane > 0 ? (
                                <p className="text-blue-300 mb-2">
                                    🎳 {t('player.lanes')} {match.team1Lane}-{match.team2Lane} | {gamesCount} {t('player.gamesPerMatch')}
                                </p>
                            ) : (
                                <p className="text-gray-400 italic mb-2">
                                    {t('player.lanesNotAssigned')} | {gamesCount} {t('player.gamesPerMatch')}
                                </p>
                            )}
                            {matchResults && (
                                <div className="flex justify-center space-x-8 mb-4">
                                    <div className="text-center">
                                        <div className="text-blue-300 text-sm">{match.team1Name} {t('player.points')}</div>
                                        <div className="text-white font-bold text-2xl">{matchResults.team1Points || 0}</div>
                                    </div>
                                    <div className="text-white text-xl">-</div>
                                    <div className="text-center">
                                        <div className="text-purple-300 text-sm">{match.team2Id === 0 ? (t('player.blind') || 'Blind') : match.team2Name} {t('player.points')}</div>
                                        <div className="text-white font-bold text-2xl">{matchResults.team2Points || 0}</div>
                                    </div>
                                </div>
                            )}
                            {!matchResults && (
                                <div className="text-gray-400 italic text-sm">
                                    {t('player.noScoresRecorded')}
                                </div>
                            )}
                        </div>
                    </div>
                    {/* Scoring Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Team 1 */}
                        <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                            <h3 className="text-xl font-bold text-white mb-4 text-center">{match.team1Name}</h3>
                            {/* Team 1 Members */}
                            <div className="space-y-4 mb-6">
                                {team1Members.map((member, index) => {
                                    // Check if any game for this player has backup info
                                    const playerBackupInfo = Object.entries(backupInfo).find(([key]) => 
                                        key.startsWith(`${member.playerId}-`)
                                    )?.[1];
                                    
                                    // If backup played, show the backup bowler's card instead
                                    const displayName = playerBackupInfo ? playerBackupInfo.backupBowlerName : member.playerName;
                                    const displayAverage = member.average; // Keep original roster member's avg/hdcp for reference
                                    const displayHandicap = member.handicap;
                                    
                                    return (
                                    <div key={member.playerId} className="space-y-2">
                                        {/* Show backup indicator if backup played */}
                                        {playerBackupInfo && (
                                            <div className="bg-amber-900/20 border border-amber-400/30 rounded-lg p-2">
                                                <div className="flex items-center space-x-2 text-xs">
                                                    <span className="text-amber-400">🔄</span>
                                                    <span className="text-amber-300">
                                                        {playerBackupInfo.backupBowlerName} {t('matchScoring.backupFor')} {member.playerName}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                        
                                        <div
                                            className="bg-white/5 rounded-lg p-4 border border-white/10"
                                        >
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center space-x-3">
                                                <div className="flex items-center justify-center w-8 h-8 bg-blue-600 rounded-full text-white font-bold text-sm">
                                                    {index + 1}
                                                </div>
                                                <div>
                                                    <h4 className="text-white font-semibold">{displayName}</h4>
                                                    <div className="flex items-center space-x-4 text-sm">
                                                        <div className="flex items-center space-x-1">
                                                            <span className="text-blue-300">{t('player.avg')}:</span>
                                                            <span className="text-white">{displayAverage}</span>
                                                        </div>
                                                        <div className="flex items-center space-x-1">
                                                            <span className="text-green-300">{t('player.hdcp')}:</span>
                                                            <span className="text-white">{displayHandicap}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {/* Game Scores */}
                                        <div className="grid grid-cols-3 gap-2 mb-3">
                                            {Array.from({ length: gamesCount }, (_, gameIndex) => {
                                                const gameNumber = gameIndex + 1;
                                                const scoreKey = `${member.playerId}-${gameNumber}`;
                                                const score = scores[scoreKey];
                                                return (
                                                    <div key={gameNumber} className="text-center">
                                                        <label className="block text-xs text-blue-300 mb-1">{t('player.game')} {gameNumber}</label>
                                                        <div className="w-full px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-center">
                                                            {score ?? ''}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        {/* Player Series Totals */}
                                        <div className="grid grid-cols-2 gap-4 text-sm">
                                            <div className="text-center">
                                                <div className="text-blue-300">{t('player.series')}</div>
                                                <div className="text-white font-bold">
                                                    {calculatePlayerSeries(member.playerId)}
                                                </div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-green-300">{t('player.withHandicap')}</div>
                                                <div className="text-white font-bold">
                                                    {calculatePlayerSeries(member.playerId, true)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                );
                            })}
                            </div>
                            {/* Team 1 Totals */}
                            <div className="bg-blue-600/20 rounded-lg p-4 border border-blue-400/30">
                                <h4 className="text-white font-bold mb-3 text-center">{t('player.teamTotals')}</h4>
                                {/* Game Totals */}
                                <div className="grid grid-cols-3 gap-2 mb-3">
                                    {Array.from({ length: gamesCount }, (_, gameIndex) => {
                                        const gameNumber = gameIndex + 1;
                                        const gameTotal = calculateTeamGameTotal(team1Members, gameNumber);
                                        const gameHandicapTotal = calculateTeamGameTotal(team1Members, gameNumber, true);
                                        const gameWinner = getGameWinner(gameNumber);
                                        console.log('Game', gameNumber, 'Winner:', gameWinner, 'matchResults:', matchResults);
                                        return (
                                            <div key={gameNumber} className={`text-center relative ${matchResults && gameWinner === 2 ? 'opacity-50 blur-[1px]' : ''}`}>
                                                {matchResults && gameWinner === 1 && (
                                                    <div className="absolute -top-1 -right-1 bg-green-500 rounded-full p-1 z-10">
                                                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    </div>
                                                )}
                                                {matchResults && gameWinner === 2 && (
                                                    <div className="absolute -top-1 -right-1 bg-red-500 rounded-full p-1 z-10">
                                                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                    </div>
                                                )}
                                                <div className="text-xs text-blue-300 mb-1">{t('player.game')} {gameNumber}</div>
                                                <div className="text-white font-bold">{gameTotal}</div>
                                                <div className="text-green-300 text-sm">{t('player.withHandicap')}: <span className="font-bold text-white">{gameHandicapTotal}</span></div>
                                            </div>
                                        );
                                    })}
                                </div>
                                {/* Series Totals */}
                                <div className={`grid grid-cols-2 gap-4 text-center border-t border-blue-400/30 pt-3 relative ${matchResults && getSeriesWinnerHandicap() === 2 ? 'opacity-50 blur-[1px]' : ''}`}>
                                    {matchResults && getSeriesWinnerHandicap() === 1 && (
                                        <div className="absolute -top-2 -right-2 bg-green-500 rounded-full p-2 z-10">
                                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                    )}
                                    {matchResults && getSeriesWinnerHandicap() === 2 && (
                                        <div className="absolute -top-2 -right-2 bg-red-500 rounded-full p-2 z-10">
                                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </div>
                                    )}
                                    <div>
                                        <div className="text-blue-300 text-sm">{t('player.teamSeries')}</div>
                                        <div className="text-white font-bold text-xl">
                                            {calculateTeamSeries(team1Members)}
                                        </div>
                                    </div>
                                    <div className={`relative ${matchResults && getSeriesWinnerHandicap() === 2 ? 'opacity-50 blur-[1px]' : ''}`}>
                                        <div className="text-green-300 text-sm">{t('player.withHandicap')}</div>
                                        <div className="text-white font-bold text-xl">
                                            {calculateTeamSeries(team1Members, true)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* Team 2 */}
                        <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                            <h3 className="text-xl font-bold text-white mb-4 text-center">
                                {match.team2Id === 0 ? (t('player.blind') || 'Blind') : match.team2Name}
                            </h3>
                            
                            {/* Bot Blind Section */}
                            {match.team2Id === 0 ? (
                                <div className="bg-purple-500/20 border border-purple-400/40 rounded-lg p-6 text-center">
                                    <h4 className="text-2xl font-bold text-purple-200 mb-2">{t('player.blind') || 'Blind'}</h4>
                                    <p className="text-purple-300 text-sm">{t('player.botBlind.info')}</p>
                                </div>
                            ) : (
                            /* Team 2 Members */
                            <div className="space-y-4 mb-6">{team2Members.map((member, index) => {
                                    // Check if any game for this player has backup info
                                    const playerBackupInfo = Object.entries(backupInfo).find(([key]) => 
                                        key.startsWith(`${member.playerId}-`)
                                    )?.[1];
                                    
                                    // If backup played, show the backup bowler's card instead
                                    const displayName = playerBackupInfo ? playerBackupInfo.backupBowlerName : member.playerName;
                                    const displayAverage = member.average; // Keep original roster member's avg/hdcp for reference
                                    const displayHandicap = member.handicap;
                                    
                                    return (
                                    <div key={member.playerId} className="space-y-2">
                                        {/* Show backup indicator if backup played */}
                                        {playerBackupInfo && (
                                            <div className="bg-amber-900/20 border border-amber-400/30 rounded-lg p-2">
                                                <div className="flex items-center space-x-2 text-xs">
                                                    <span className="text-amber-400">🔄</span>
                                                    <span className="text-amber-300">
                                                        {playerBackupInfo.backupBowlerName} {t('matchScoring.backupFor')} {member.playerName}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                        
                                        <div
                                            className="bg-white/5 rounded-lg p-4 border border-white/10"
                                        >
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center space-x-3">
                                                <div className="flex items-center justify-center w-8 h-8 bg-purple-600 rounded-full text-white font-bold text-sm">
                                                    {index + 1}
                                                </div>
                                                <div>
                                                    <h4 className="text-white font-semibold">{displayName}</h4>
                                                    <div className="flex items-center space-x-4 text-sm">
                                                        <div className="flex items-center space-x-1">
                                                            <span className="text-blue-300">{t('player.avg')}:</span>
                                                            <span className="text-white">{displayAverage}</span>
                                                        </div>
                                                        <div className="flex items-center space-x-1">
                                                            <span className="text-green-300">{t('player.hdcp')}:</span>
                                                            <span className="text-white">{displayHandicap}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {/* Game Scores */}
                                        <div className="grid grid-cols-3 gap-2 mb-3">
                                            {Array.from({ length: gamesCount }, (_, gameIndex) => {
                                                const gameNumber = gameIndex + 1;
                                                const scoreKey = `${member.playerId}-${gameNumber}`;
                                                const score = scores[scoreKey];
                                                return (
                                                    <div key={gameNumber} className="text-center">
                                                        <label className="block text-xs text-purple-300 mb-1">{t('player.game')} {gameNumber}</label>
                                                        <div className="w-full px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-center">
                                                            {score ?? ''}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        {/* Player Series Totals */}
                                        <div className="grid grid-cols-2 gap-4 text-sm">
                                            <div className="text-center">
                                                <div className="text-blue-300">{t('player.series')}</div>
                                                <div className="text-white font-bold">
                                                    {calculatePlayerSeries(member.playerId)}
                                                </div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-green-300">{t('player.withHandicap')}</div>
                                                <div className="text-white font-bold">
                                                    {calculatePlayerSeries(member.playerId, true)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                );
                            })}</div>
                            )}
                            
                            {/* Team 2 Totals - Skip for Bot Blind */}
                            {match.team2Id !== 0 && (
                            <div className="bg-purple-600/20 rounded-lg p-4 border border-purple-400/30">
                                <h4 className="text-white font-bold mb-3 text-center">{t('player.teamTotals')}</h4>
                                {/* Game Totals */}
                                <div className="grid grid-cols-3 gap-2 mb-3">
                                    {Array.from({ length: gamesCount }, (_, gameIndex) => {
                                        const gameNumber = gameIndex + 1;
                                        
                                        // For Bot Blind, calculate from average instead of querying members
                                        let gameTotal, gameHandicapTotal;
                                        if (match?.team2Id === 0 && blindConfig?.botBlindAverage) {
                                            const botBlindGameTotal = blindConfig.botBlindAverage * team1Members.length;
                                            gameTotal = botBlindGameTotal;
                                            gameHandicapTotal = botBlindGameTotal; // No handicap for Bot Blind
                                        } else {
                                            gameTotal = calculateTeamGameTotal(team2Members, gameNumber);
                                            gameHandicapTotal = calculateTeamGameTotal(team2Members, gameNumber, true);
                                        }
                                        
                                        const gameWinner = getGameWinner(gameNumber);
                                        console.log('Game', gameNumber, 'Winner:', gameWinner, 'matchResults:', matchResults);
                                        return (
                                            <div key={gameNumber} className={`text-center relative ${matchResults && gameWinner === 1 ? 'opacity-50 blur-[1px]' : ''}`}>
                                                {matchResults && gameWinner === 2 && (
                                                    <div className="absolute -top-1 -right-1 bg-green-500 rounded-full p-1 z-10">
                                                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    </div>
                                                )}
                                                {matchResults && gameWinner === 1 && (
                                                    <div className="absolute -top-1 -right-1 bg-red-500 rounded-full p-1 z-10">
                                                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                    </div>
                                                )}
                                                <div className="text-xs text-purple-300 mb-1">{t('player.game')} {gameNumber}</div>
                                                <div className="text-white font-bold">{gameTotal}</div>
                                                <div className="text-green-300 text-sm">{t('player.withHandicap')}: <span className="font-bold text-white">{gameHandicapTotal}</span></div>
                                            </div>
                                        );
                                    })}
                                </div>
                                {/* Series Totals */}
                                <div className={`grid grid-cols-2 gap-4 text-center border-t border-purple-400/30 pt-3 relative ${matchResults && getSeriesWinnerHandicap() === 1 ? 'opacity-50 blur-[1px]' : ''}`}>
                                    {matchResults && getSeriesWinnerHandicap() === 2 && (
                                        <div className="absolute -top-2 -right-2 bg-green-500 rounded-full p-2 z-10">
                                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                    )}
                                    {matchResults && getSeriesWinnerHandicap() === 1 && (
                                        <div className="absolute -top-2 -right-2 bg-red-500 rounded-full p-2 z-10">
                                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </div>
                                    )}
                                    <div>
                                        <div className="text-purple-300 text-sm">{t('player.teamSeries')}</div>
                                        <div className="text-white font-bold text-xl">
                                            {match?.team2Id === 0 && blindConfig?.botBlindAverage
                                                ? (blindConfig.botBlindAverage * team1Members.length * gamesCount)
                                                : calculateTeamSeries(team2Members)
                                            }
                                        </div>
                                    </div>
                                    <div className={`relative ${matchResults && getSeriesWinnerHandicap() === 1 ? 'opacity-50 blur-[1px]' : ''}`}>
                                        <div className="text-green-300 text-sm">{t('player.withHandicap')}</div>
                                        <div className="text-white font-bold text-xl">
                                            {match?.team2Id === 0 && blindConfig?.botBlindAverage
                                                ? (blindConfig.botBlindAverage * team1Members.length * gamesCount)
                                                : calculateTeamSeries(team2Members, true)
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                            )}
                        
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MatchScoringModal;